"use client"
import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'

interface NewsletterFormProps {
  source: 'footer' | 'article' | 'sidebar'
  variant?: 'default' | 'compact' | 'gradient'
  placeholder?: string
  buttonText?: string
  className?: string
}

export default function NewsletterForm({ 
  source, 
  variant = 'default', 
  placeholder,
  buttonText,
  className = ''
}: NewsletterFormProps) {
  const locale = useLocale()
  const tn = useTranslations('newsletter')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  
  // Utiliser les traductions par défaut si pas de props
  const actualPlaceholder = placeholder || tn('placeholder')
  const actualButtonText = buttonText || tn('button')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!email.trim()) {
      setStatus('error')
      setMessage(tn('emailRequired'))
      return
    }

    setStatus('loading')
    setMessage('')

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_BASE_URL}/api/newsletter/subscribe/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: email.trim().toLowerCase(), 
          source, 
          locale 
        })
      })

      const data = await response.json()
      
      if (data.success) {
        setStatus('success')
        setMessage(data.message)
        setEmail('')
        
        // Auto-reset après 5 secondes
        setTimeout(() => {
          setStatus('idle')
          setMessage('')
        }, 5000)
      } else {
        setStatus('error')
        setMessage(data.error || tn('genericError'))
      }
    } catch (error) {
      setStatus('error')
      setMessage(tn('connectionError'))
    }
  }

  // Styles selon la variante
  const getInputClasses = () => {
    const baseClasses = "flex-1 px-4 py-2 rounded-lg border focus:outline-none focus:ring-2 transition-colors"
    
    switch (variant) {
      case 'gradient':
        return `${baseClasses} text-gray-900 placeholder-gray-500 focus:ring-white border-transparent`
      case 'compact':
        return `${baseClasses} text-sm border-gray-300 focus:ring-purple-500 focus:border-purple-500`
      default:
        return `${baseClasses} bg-gray-800 text-white placeholder-gray-400 border-0 focus:ring-blue-500`
    }
  }

  const getButtonClasses = () => {
    const baseClasses = "px-6 py-2 font-semibold rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
    
    switch (variant) {
      case 'gradient':
        return `${baseClasses} bg-white text-blue-600 hover:bg-gray-100`
      case 'compact':
        return `${baseClasses} text-sm bg-purple-600 text-white hover:bg-purple-700`
      default:
        return `${baseClasses} bg-blue-600 text-white hover:bg-blue-700`
    }
  }

  const getMessageClasses = () => {
    const baseClasses = "mt-2 text-sm flex items-center gap-1"
    return status === 'success' 
      ? `${baseClasses} text-green-600` 
      : `${baseClasses} text-red-600`
  }

  return (
    <div className={className}>
      <form onSubmit={handleSubmit}>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={actualPlaceholder}
            required
            disabled={status === 'loading'}
            className={getInputClasses()}
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className={getButtonClasses()}
          >
            {status === 'loading' ? (
              <div className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {tn('loading')}
              </div>
            ) : (
              actualButtonText
            )}
          </button>
        </div>
        
        {message && (
          <p className={getMessageClasses()}>
            {status === 'success' ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {message}
          </p>
        )}
      </form>
    </div>
  )
}